/**
 * Raw JSON edit pane (FEAT-2026-0005, Phase 2).
 *
 * Monaco editor with the per-template JSON Schema attached for full
 * IntelliSense — autocomplete on keys, hover docs, error squiggles on
 * invalid values, etc. Heavy bundle (~3 MB) but acceptable for an internal
 * settings page; lazy-loaded by a Suspense boundary above.
 *
 * Edits flow into the same zustand store as the form pane, so undo/redo
 * works across panes and the live preview re-renders identically.
 */

import { useEffect, useState } from 'react';
import { Alert, Box } from '@mui/material';
import Editor, { useMonaco } from '@monaco-editor/react';
import { useReportTemplateStore } from './useReportTemplateStore';
import { reportTemplateService } from '../../../services/reportTemplateService';

interface Props {
  templateKey: string;
}

const SCHEMA_URI = 'inmemory://report-template-schema.json';

export const JsonPane = ({ templateKey }: Props) => {
  const draftConfig = useReportTemplateStore((s) => s.draftConfig);
  const setDraftConfig = useReportTemplateStore((s) => s.setDraftConfig);
  const monaco = useMonaco();

  const [parseError, setParseError] = useState<string | null>(null);

  // Pretty-print the draft so the editor opens with formatted JSON.
  const value = draftConfig ? JSON.stringify(draftConfig, null, 2) : '';

  // Wire the JSON Schema into Monaco so the user gets IntelliSense.
  useEffect(() => {
    if (!monaco) return;
    let cancelled = false;
    reportTemplateService.getSchema(templateKey).then((schema) => {
      if (cancelled) return;
      monaco.languages.json.jsonDefaults.setDiagnosticsOptions({
        validate: true,
        schemas: [{ uri: SCHEMA_URI, fileMatch: ['*'], schema: schema as object }],
      });
    });
    return () => { cancelled = true; };
  }, [monaco, templateKey]);

  const onChange = (next: string | undefined) => {
    if (next == null) return;
    try {
      const parsed = JSON.parse(next);
      setParseError(null);
      setDraftConfig(parsed);
    } catch (err: any) {
      setParseError(err?.message ?? 'Invalid JSON');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {parseError && <Alert severity="warning" sx={{ borderRadius: 0 }}>{parseError}</Alert>}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <Editor
          height="100%"
          defaultLanguage="json"
          value={value}
          onChange={onChange}
          theme="vs-dark"
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            tabSize: 2,
            scrollBeyondLastLine: false,
            wordWrap: 'on',
          }}
        />
      </Box>
    </Box>
  );
};
